import { CustomDetector } from 'i18next-browser-languagedetector';

const CHINA_COUNTRY_CODES = ['CN', 'HK', 'MO'];
const TAIWAN_COUNTRY_CODES = ['TW'];
const IPAPI_ENDPOINT = 'https://ipapi.co/json/';
const CACHE_DURATION_DAYS = 30;

interface IpApiResponse {
  country_code: string;
  country_name: string;
  error?: boolean;
}

// Async function to detect and cache language
async function detectLanguageAsync() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

    const response = await fetch(IPAPI_ENDPOINT, {
      signal: controller.signal,
      cache: 'default', // Use browser cache
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: IpApiResponse = await response.json();

    if (data.error) {
      throw new Error('IP API error response');
    }

    const countryCode = data.country_code;

    // Map country to language and cache it
    let detectedLanguage: string | undefined;
    if (CHINA_COUNTRY_CODES.includes(countryCode)) {
      detectedLanguage = 'zh-CN';
    } else if (TAIWAN_COUNTRY_CODES.includes(countryCode)) {
      detectedLanguage = 'zh-TW';
    }

    if (detectedLanguage) {
      try {
        localStorage.setItem('capitol-trades-language', detectedLanguage);
        localStorage.setItem('capitol-trades-language-timestamp', Date.now().toString());
        // Reload the page to apply the detected language
        window.location.reload();
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  } catch (error) {
    console.warn('Geolocation detection failed:', error);
  }
}

export const geolocationDetector: CustomDetector = {
  name: 'geolocation',

  lookup() {
    // Check cache first
    try {
      const cached = localStorage.getItem('capitol-trades-language');
      const timestamp = localStorage.getItem('capitol-trades-language-timestamp');

      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        const cacheExpiry = CACHE_DURATION_DAYS * 24 * 60 * 60 * 1000; // 30 days in ms

        if (age < cacheExpiry) {
          return cached;
        }
      }
    } catch (e) {
      // Ignore localStorage errors (private browsing, etc.)
    }

    // Since we can't use async in the lookup function,
    // we'll trigger the async fetch but return undefined immediately
    // The language will be cached and used on the next page load
    detectLanguageAsync();

    // Return undefined to fall through to next detector (browser language)
    return undefined;
  },

  cacheUserLanguage(lng: string) {
    // Store detected language in localStorage for future visits
    try {
      localStorage.setItem('capitol-trades-language', lng);
      localStorage.setItem('capitol-trades-language-timestamp', Date.now().toString());
    } catch (e) {
      // Ignore localStorage errors (private browsing, etc.)
    }
  },
};
